/**
 * useResults Hook
 * Custom hook for managing results data fetching and state
 * 
 * Updated to handle separate profile report (instant) and recommendations (async LLM)
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getResults, getProfileReport, getRecommendations } from '../services/api';
import { ResultData, TabType, ProfileReportData, RecommendationsData } from '../types/results';

interface UseResultsReturn {
  results: ResultData | null;
  loading: boolean;
  loadingProfile: boolean;
  loadingRecommendations: boolean;
  profileReport: ProfileReportData | null;
  recommendations: RecommendationsData | null;
  activeTab: TabType;
  setActiveTab: (tab: TabType) => void;
  loadProfileReport: () => Promise<void>;
  loadRecommendations: () => Promise<void>;
  // Legacy support
  loadProfileAnalysis: () => Promise<void>;
}

export const useResults = (attemptId: string | undefined): UseResultsReturn => {
  const [results, setResults] = useState<ResultData | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [loadingRecommendations, setLoadingRecommendations] = useState(false);
  const [profileReport, setProfileReport] = useState<ProfileReportData | null>(null);
  const [recommendations, setRecommendations] = useState<RecommendationsData | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('summary');
  const navigate = useNavigate();

  // Load initial results
  const loadResults = useCallback(async () => {
    if (!attemptId || attemptId === 'undefined') {
      navigate('/dashboard');
      return;
    }

    try {
      const response = await getResults(Number(attemptId));
      setResults(response.data);
    } catch (err) {
      console.error('Error loading results:', err);
      alert('Erreur lors du chargement des résultats');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  }, [attemptId, navigate]);

  // Load profile report (instant, no LLM)
  const loadProfileReport = useCallback(async () => {
    if (!attemptId || loadingProfile || profileReport) return;
    
    setLoadingProfile(true);
    try {
      console.log('🚀 Requesting instant profile report for attempt:', attemptId);
      const response = await getProfileReport(Number(attemptId));
      console.log('✅ Profile report received:', response.data.profile_name);
      
      setProfileReport(response.data);
      
      // Also update results with the formatted report for backward compatibility
      if (response.data?.formatted_report) {
        setResults(prev => prev ? {
          ...prev,
          profile_report: response.data,
          profile_analysis: response.data.formatted_report
        } : null);
      }
    } catch (err: any) {
      console.error('❌ Error loading profile report:', err);
      alert(`Erreur lors du chargement du profil: ${err.response?.data?.detail || err.message || 'Erreur inconnue'}`);
    } finally {
      setLoadingProfile(false);
    }
  }, [attemptId, loadingProfile, profileReport]);

  // Load recommendations (async LLM)
  const loadRecommendations = useCallback(async () => {
    if (!attemptId || loadingRecommendations || recommendations) return;
    
    setLoadingRecommendations(true);
    try {
      console.log('🚀 Requesting LLM recommendations for attempt:', attemptId);
      const response = await getRecommendations(Number(attemptId));
      console.log('✅ Recommendations received:', response.data.from_cache ? '(from cache)' : '(fresh)');
      
      setRecommendations(response.data);
      
      // Update results
      setResults(prev => prev ? {
        ...prev,
        recommendations: response.data
      } : null);
    } catch (err: any) {
      console.error('❌ Error loading recommendations:', err);
      // Don't show alert for recommendations, they're optional
      console.warn('Recommendations could not be loaded, continuing without them');
    } finally {
      setLoadingRecommendations(false);
    }
  }, [attemptId, loadingRecommendations, recommendations]);

  // Legacy support: load profile analysis (calls both)
  const loadProfileAnalysis = useCallback(async () => {
    await loadProfileReport();
    // Start loading recommendations in background
    loadRecommendations();
  }, [loadProfileReport, loadRecommendations]);

  // Initial load
  useEffect(() => {
    loadResults();
  }, [loadResults]);

  // Auto-load profile when switching to profile tab
  useEffect(() => {
    if (activeTab === 'profile' && results && !profileReport && !loadingProfile) {
      loadProfileReport();
    }
  }, [activeTab, results, profileReport, loadingProfile, loadProfileReport]);

  // Auto-load recommendations when switching to recommendations tab
  useEffect(() => {
    if (activeTab === 'recommendations' && results && !recommendations && !loadingRecommendations) {
      // First ensure profile is loaded (needed for context)
      if (!profileReport && !loadingProfile) {
        loadProfileReport();
      }
      loadRecommendations();
    }
  }, [activeTab, results, recommendations, loadingRecommendations, profileReport, loadingProfile, loadProfileReport, loadRecommendations]);

  return {
    results,
    loading,
    loadingProfile,
    loadingRecommendations,
    profileReport,
    recommendations,
    activeTab,
    setActiveTab,
    loadProfileReport,
    loadRecommendations,
    loadProfileAnalysis
  };
};
